<template>
  <div class="despesas-status-form">
    <div class="form-row">
      <div class="form-field full-width">
        <input-text
          :label="t('Título')"
          :placeholder="t('Título')"
          :validation="validateTitulo"
          :required="true"
          elem-id="despesa_status_titulo"
          data-testid="despesas-status-input-titulo"
          v-model="formData.despesa_status_titulo">
        </input-text>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDespesasStatus } from '@/composables/useDespesasStatus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface DespesaStatus {
  despesa_status_id: Id;
  despesa_status_titulo?: string;
  [key: string]: unknown;
}

interface DespesaStatusFormData {
  despesa_status_titulo: string;
}

const props = withDefaults(defineProps<{
  isEditMode?: boolean;
  status?: DespesaStatus | null;
}>(), {
  isEditMode: false,
  status: null
});

const emit = defineEmits<{
  'save-success': [];
}>();

const { t } = useI18n();

const { createStatus, updateStatus } = useDespesasStatus();

const isLoading = ref<boolean>(false);

const formData = reactive<DespesaStatusFormData>({
  despesa_status_titulo: ''
});

const loadForEdit = (status: DespesaStatus): void => {
  log('DespesasStatusForm', 'loadForEdit', 'Begin', status);

  const parsed: DespesaStatusFormData = {
    despesa_status_titulo: status.despesa_status_titulo || ''
  };

  log('DespesasStatusForm', 'loadForEdit', 'Parsed values', parsed);
  Object.assign(formData, parsed);
  log('DespesasStatusForm', 'loadForEdit', 'formData after assign', { ...formData });
};

const resetForm = (): void => {
  log('DespesasStatusForm', 'resetForm', 'Resetting form');
  Object.assign(formData, {
    despesa_status_titulo: ''
  });
};

const initializeForNew = (): void => {
  log('DespesasStatusForm', 'initializeForNew', 'Initializing form for new status');
  resetForm();
};

const isTituloValid = computed<boolean>(() => {
  return formData.despesa_status_titulo.trim().length > 0;
});

const validateTitulo = (): boolean => isTituloValid.value;

const canSave = computed<boolean>(() => {
  return isTituloValid.value && !isLoading.value;
});

const saveStatus = async (): Promise<void> => {
  log('DespesasStatusForm', 'saveStatus', 'Attempt save', {
    isEditMode: props.isEditMode,
    formData: formData
  });

  if (!canSave.value) {
    log('DespesasStatusForm', 'saveStatus', 'Form validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const dataToSave = {
      ...formData
    };

    if (props.isEditMode && props.status) {
      await updateStatus(props.status.despesa_status_id, dataToSave);
      log('DespesasStatusForm', 'saveStatus', 'Status updated successfully');
    } else {
      await createStatus(dataToSave);
      log('DespesasStatusForm', 'saveStatus', 'Status created successfully');
    }

    emit('save-success');
  } catch (error) {
    log('DespesasStatusForm', 'saveStatus', 'Error saving status', error);
    throw error;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.status, (newStatus) => {
  if (newStatus && props.isEditMode) {
    log('DespesasStatusForm', 'watch:status', 'Loading status data', newStatus);
    loadForEdit(newStatus);
  }
}, { immediate: true });

defineExpose({
  loadForEdit,
  initializeForNew,
  saveStatus,
  resetForm,
  canSave
});
</script>

<style scoped>
.despesas-status-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.full-width {
  flex: 1 1 100%;
}

.form-group {
  margin-bottom: 15px;
}

.text-danger {
  color: #dd4b39;
}

.input-group-addon {
  background-color: #f4f4f4;
  border: 1px solid #d2d6de;
  border-left: 0;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #555;
  text-align: center;
}

.is-invalid {
  border-color: #dd4b39;
}

.invalid-feedback {
  color: #dd4b39;
  font-size: 12px;
  margin-top: 5px;
}
</style>
